// The Advanced drawer's mode writes (#5211, epic #5200) — a mode switch or a
// tools edit is a FULL-ROW PUT `/v1/agent-modes{,/repo/:repoId}` carrying the
// view's OTHER field unedited (a switch keeps `code_tools`; a tools edit keeps
// `enabled`) and the #5208 `expect` precondition.
//
// RE-READ, NO OPTIMISTIC PAINT (the drawer's design decision, per the issue's
// "Writes" section): unlike `lib/agent-model-save.ts`'s route pins, a mode write
// here never patches the cache ahead of the server — it PUTs, then always
// refetches, win or lose. That keeps `apps/dashboard/CLAUDE.md` § Boundary's
// optimistic-write exception list closed; a `ConfirmDialog` already makes the
// switch a deliberate, non-instant act, so the extra half-second to the re-read
// is not a regression a customer would notice.
//
// `routeRefusalOf` is REUSED, not re-written: `staleConflict`
// (`apps/api/src/routes/stale-conflict.ts`) renders the identical 409 `config-stale`
// shape for a route AND a mode write, so one parser serves both — and a mode's
// non-reasoning 422 (`invalid_mode`) already lands in its generic `refused`
// bucket, which is exactly the right "no cure, show the server's detail" answer.
//
// UNDO (round 3, #5381), same shape as `agent-model-save.ts`'s: a save's toast
// carries an Undo guarded by the VERSION THIS WRITE JUST PRODUCED (the PUT
// echo's `revision`, `agent-modes.ts`'s `putAgentMode`) — never the stale
// `view.revision` it was read at, which a teammate's write landing in the
// toast's few seconds would have already moved past. What Undo REPLAYS depends
// on whether this scope owned the row BEFORE the write ({@link canReset} on the
// pre-write `view`): if it did, Undo re-PUTs the prior `{enabled, code_tools}`
// at this scope; if the write just CREATED the override (the prior value was
// inherited from another scope), Undo instead RESETS it — a re-PUT would leave
// an override standing where none existed. Every intent — the write and its
// Undo alike — carries the account it was made under (`tenantNow`,
// `guarded-save.ts`) and refuses unsent if the page has since switched org.
//
// ROUND 4 (#5381): the lock, the per-mode refusal store and the org-switch
// cleanup effect are `guarded-save.ts`'s (one-impl) — this file no longer
// hand-rolls them. `Agents.tsx` wires all three save controllers' `notify` AND
// `dismissNotice` to the page's ONE toast, so a switch takes every controller's
// standing notice down together.

import type { AgentModeKey } from '@developerz/domain';
import { t } from '../i18n';
import {
  type AgentModeExpect,
  type AgentModesScope,
  type AgentModeView,
  canReset,
  putAgentMode,
  resetAgentMode,
} from './agent-modes';
import { createGuardedSaves, type Tenant, tenantNow } from './guarded-save';
import { type RouteRefusal, routeRefusalOf } from './route-refusal';
import type { ToastAction, ToastTone } from './toast';

/** `createGuardedSaves`'s intent bound needs only the tenant here: a mode
 * write's own retry (Undo) is built from closures over `view`, never replayed
 * from a stored intent, so there is nothing else to carry. */
type ModeIntent = { readonly tenant: Tenant };

/** One write's full intended row — the field the act targets, the other kept as read. */
type ModePatch = { readonly enabled: boolean; readonly code_tools: readonly string[] | null };

export type ModeSaves = {
  /** True while THIS mode has a write in flight. */
  readonly isBusy: (key: AgentModeKey) => boolean;
  /** The mode's standing refusal, or null. */
  readonly refusal: (key: AgentModeKey) => RouteRefusal | null;
  readonly dismiss: (key: AgentModeKey) => void;
  /** Flip `enabled`; the write keeps the view's own `code_tools`. */
  readonly setEnabled: (view: AgentModeView, enabled: boolean) => Promise<void>;
  /** Replace `code_tools`; the write keeps the view's own `enabled`. */
  readonly setTools: (view: AgentModeView, codeTools: readonly string[] | null) => Promise<void>;
};

/**
 * The #5208 precondition for a write over `view`, read at `scope`: this scope's
 * own revision when it owns the winning row ({@link canReset}), else
 * `{absent: true}` — no override at THIS scope was read, org or repo alike.
 */
export function expectFor(scope: AgentModesScope, view: AgentModeView): AgentModeExpect {
  return canReset(scope, view) ? { revision: view.revision } : { absent: true };
}

/** One scope's mode-write controller — one instance per drawer/panel mount. */
export function createModeSaves(input: {
  readonly scope: AgentModesScope;
  readonly token: string | null;
  /** Re-read after every write, win or lose — see the file header. */
  readonly refetch: () => Promise<unknown>;
  /** The page's ONE toast — the same `notify` `agent-model-save.ts` and
   * `repo-route-save.ts` share, so an org switch's "drop the standing toast"
   * effect (now `guarded-save.ts`'s, one-impl) takes THIS controller's Undo
   * down too. */
  readonly notify: (message: string, tone?: ToastTone, action?: ToastAction) => void;
  /** Take the page's toast down — an org switch strands its Undo/refusal in
   * the other org (`guarded-save.ts`). */
  readonly dismissNotice: () => void;
}): ModeSaves {
  const guarded = createGuardedSaves<ModeIntent>({
    fallback: t('agent_modes.save_error'),
    dismissNotice: input.dismissNotice,
    reread: () => input.refetch(),
  });

  /**
   * One full-row PUT at this controller's scope. `undoOf`, given the version
   * THIS put produces, builds the toast's Undo action — omitted for an Undo's
   * OWN replay, which offers no further undo (mirrors `agent-model-save.ts`).
   */
  const put = (
    key: AgentModeKey,
    patch: ModePatch,
    expect: AgentModeExpect,
    tenant: Tenant,
    undoOf: ((savedRevision: number) => ToastAction) | undefined,
  ): Promise<void> =>
    guarded.run(key, { tenant }, async (ctx) => {
      ctx.clearRefusal();
      try {
        const saved = await putAgentMode(
          input.scope,
          { mode_key: key, enabled: patch.enabled, code_tools: patch.code_tools, expect },
          input.token,
        );
        if (!ctx.moved()) input.notify(t('agent_modes.saved'), 'success', undoOf?.(saved.revision));
      } catch (err) {
        ctx.refuse(routeRefusalOf(err));
      }
    });

  /** Undo of a put that CREATED the override here: drop it, guarded by the
   * version that put produced — a re-PUT would leave an override standing
   * where the prior value was inherited from another scope. */
  const reset = (key: AgentModeKey, expectRevision: number, tenant: Tenant): Promise<void> =>
    guarded.run(key, { tenant }, async (ctx) => {
      ctx.clearRefusal();
      try {
        await resetAgentMode(input.scope, key, input.token, expectRevision);
        if (!ctx.moved()) input.notify(t('agents.save.undone'), 'success');
      } catch (err) {
        ctx.refuse(routeRefusalOf(err));
      }
    });

  /** One user-initiated write. `view` is the row as READ, BEFORE this write —
   * {@link canReset} on it says whether this scope already owned it, which
   * decides what its Undo means (see the file header). */
  const write = (view: AgentModeView, patch: ModePatch): Promise<void> => {
    const tenant = tenantNow();
    const ownedBefore = canReset(input.scope, view);
    const prior: ModePatch = { enabled: view.enabled, code_tools: view.code_tools };
    const undoOf = (savedRevision: number): ToastAction => ({
      label: t('agents.save.undo'),
      run: () =>
        void (ownedBefore
          ? put(view.key, prior, { revision: savedRevision }, tenant, undefined)
          : reset(view.key, savedRevision, tenant)),
    });
    return put(view.key, patch, expectFor(input.scope, view), tenant, undoOf);
  };

  return {
    isBusy: guarded.isBusy,
    refusal: (key) => guarded.refusal(key)?.refusal ?? null,
    dismiss: guarded.dismiss,
    setEnabled: (view, enabled) => write(view, { enabled, code_tools: view.code_tools }),
    setTools: (view, codeTools) => write(view, { enabled: view.enabled, code_tools: codeTools }),
  };
}
